import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['errorMessage', 'cardFormWrapper', 'processingWrapper', 'cardLast4']
  
  static values = {
    paymentTermId: String,
    paymentMethodId: String
  }

  connect() {
    this.processing = false
    this.stripe = null
    
    this.boundCardAddedHandler = this.handleCardAdded.bind(this)
    this.element.addEventListener('credit-card-form:card-added', this.boundCardAddedHandler)

    if (this.hasPaymentMethodIdValue && this.paymentMethodIdValue) {
      this.fetchPaymentMethodDetails()
    }
  }

  disconnect() {
    this.element.removeEventListener('credit-card-form:card-added', this.boundCardAddedHandler)
  }

  async handleCardAdded(event) {
    if (this.processing) return
    
    const cardId = event.detail.id
    this.stripe = event.detail.stripe
    
    if (!cardId) {
      this.showError('No card ID received')
      return
    }
    
    this.setProcessing(true)
    
    try {
      const paymentIntent = await this.createPaymentIntent(cardId)
      
      if (paymentIntent.status === 'succeeded') {
        this.dispatchPaymentCompleted()
      } else if (paymentIntent.status === 'requires_action') {
        await this.handleSCA(paymentIntent, this.stripe)
      } else {
        this.showError('Payment failed. Please try again.')
        this.setProcessing(false)
      }
    } catch (error) {
      console.error('Payment error:', error)
      this.showError(error.message || 'An error occurred. Please try again.')
      this.setProcessing(false)
    }
  }

  async createPaymentIntent(sourceId) {
    const locale = document.documentElement.lang || 'en'
    const url = `/${locale}/oa/payment_intents.json`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCsrfToken()
      },
      body: JSON.stringify({
        payment_intent: {
          payment_term_id: this.paymentTermIdValue,
          source_id: sourceId
        }
      })
    })

    return this.parsePaymentIntent(response, 'Failed to create payment intent')
  }

  async handleSCA(paymentIntent, stripe) {
    try {
      const result = await stripe.paymentIntentSCA(paymentIntent.client_secret)
      
      if (result.error) {
        this.showError(result.error.message)
        this.setProcessing(false)
      } else {
        await this.confirmPaymentIntent(result.paymentIntent.id)
      }
    } catch (error) {
      console.error('SCA error:', error)
      this.showError('Authentication failed. Please try again.')
      this.setProcessing(false)
    }
  }

  async confirmPaymentIntent(paymentIntentId) {
    const locale = document.documentElement.lang || 'en'
    const url = `/${locale}/oa/payment_intents/${paymentIntentId}/confirm.json`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCsrfToken()
      },
      body: JSON.stringify({
        payment_intent: {
          payment_term_id: this.paymentTermIdValue
        }
      })
    })

    const confirmedIntent = await this.parsePaymentIntent(response, 'Failed to confirm payment')

    if (confirmedIntent.status === 'succeeded') {
      this.dispatchPaymentCompleted()
    } else {
      this.showError('Payment confirmation failed. Please try again.')
      this.setProcessing(false)
    }
  }

  // The backend intentionally returns HTTP 422 for an intent needing SCA (`requires_action`), with
  // status + client_secret in the body, so a non-2xx status here must not be treated as a hard failure
  async parsePaymentIntent(response, failureMessage) {
    const body = await response.json().catch(() => null)

    if (body && body.status) {
      return body
    }

    const serverMessage = body && (body.error || body.errors?.[0])
    throw new Error(serverMessage || `${failureMessage}: ${response.status}`)
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = 'block'
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'none'
    }
  }

  setProcessing(isProcessing) {
    this.processing = isProcessing
    
    if (this.hasCardFormWrapperTarget && this.hasProcessingWrapperTarget) {
      if (isProcessing) {
        this.cardFormWrapperTarget.style.display = 'none'
        this.processingWrapperTarget.style.display = 'block'
      } else {
        this.cardFormWrapperTarget.style.display = 'block'
        this.processingWrapperTarget.style.display = 'none'
      }
    }
  }

  async fetchPaymentMethodDetails() {
    if (!this.hasCardLast4Target) return

    const locale = document.documentElement.lang || 'en'
    
    try {
      const response = await fetch(`/${locale}/oa/payment_methods/${this.paymentMethodIdValue}.json`, {
        headers: {
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) throw new Error('Failed to fetch payment method')
      
      const data = await response.json()
      this.cardLast4Target.textContent = data.last4 || '****'
    } catch (error) {
      console.error('Error fetching payment method:', error)
      this.cardLast4Target.textContent = '****'
    }
  }

  async payWithExistingCard(event) {
    event.preventDefault()
    
    if (this.processing || !this.paymentMethodIdValue) return
    
    this.setProcessing(true)
    
    try {
      const paymentIntent = await this.createPaymentIntent(this.paymentMethodIdValue)
      
      if (paymentIntent.status === 'succeeded') {
        this.dispatchPaymentCompleted()
      } else if (paymentIntent.status === 'requires_action') {
        if (!this.stripe) {
          const StripeJS = await import('../shared/stripe_form/stripe.js')
          this.stripe = StripeJS.default
        }
        await this.handleSCA(paymentIntent, this.stripe)
      } else {
        throw new Error(`Unexpected payment intent status: ${paymentIntent.status}`)
      }
    } catch (error) {
      console.error('Payment error:', error)
      this.showError(error.message || 'Payment failed. Please try again.')
      this.setProcessing(false)
    }
  }

  dispatchPaymentCompleted() {
    this.dispatch('payment-completed', {
      detail: {
        paymentTermId: this.paymentTermIdValue
      }
    })
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
